import { Flex, Tooltip } from '@brightdrop/bd-ui';
import { cx } from '@emotion/css';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import { type MutableRefObject, useRef, useState } from 'react';

import OrganizationIcon from '~/assets/icons/leftMenu/organizationIcon.svg?react';
import ArrowDownIcon from '~/assets/icons/new/arrowDownIcon.svg?react';
import ArrowUpIcon from '~/assets/icons/new/arrowUpIcon.svg?react';
import SelectedIcon from '~/assets/icons/new/checkCircle.svg?react';
import { NAV_DRAWER_OPEN_WIDTH } from '~/features/navigationDrawer/GlobalNavDrawer.styles';

import useStyles from './FleetSwitcher.styles';

interface ContextItem {
  id: string;
  name: string;
}

interface FleetSwitcherProps {
  contexts: { root: ContextItem; subContexts: ContextItem[] };
  selectedContext: ContextItem;
  onContextSelect: (item: ContextItem) => void;
  navExpanded: boolean;
  disabled?: boolean;
}

/*
 * Despite the webkit usage, this is a standard approach for wrapping and truncating
 * long text, per https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp
 */
const SX_WRAP_TRUNCATE: SxProps = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * Displays currently-selected context with test wrapping; click to
 * display full list of available fleets
 */
const SwitcherMenuButton = ({
  buttonRef,
  menuOpen,
  navExpanded,
  handleMenuOpen,
  handleMenuClose,
  selectedContext,
  disabled = false,
}: {
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  menuOpen: boolean;
  navExpanded: boolean;
  handleMenuOpen: (clicked: boolean) => void;
  handleMenuClose: () => void;
  selectedContext: ContextItem;
  disabled?: boolean;
}) => {
  const { classes } = useStyles();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (menuOpen) {
      handleMenuClose();
    } else {
      handleMenuOpen(event.type === 'click');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleMenuOpen(false);
    }
  };

  const buttonProps = {
    'data-testid': 'fleet-switcher-menu-button',
    ref: buttonRef,
    disabled,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    className: cx(classes.buttonBase, menuOpen && classes.buttonBaseMenuOpen),
  };

  return (
    <>
      {!navExpanded ? (
        <Tooltip
          content={selectedContext.name}
          placement="right"
          showArrow
          data-testid="fleet-switcher-menu-button-tooltip"
          trigger={
            <Box>
              <ButtonBase {...buttonProps}>
                <Flex gap={12} className={classes.iconContainer}>
                  <OrganizationIcon className={classes.buttonIcon} />
                </Flex>
              </ButtonBase>
            </Box>
          }
        />
      ) : (
        <ButtonBase {...buttonProps}>
          <Flex center style={{ gap: '12px' }}>
            <Box className={classes.iconContainer}>
              <OrganizationIcon className={classes.buttonIcon} />
            </Box>

            <Flex className={classes.expandedContainer}>
              <Typography
                width="210px"
                variant="body_medium"
                sx={{
                  ...SX_WRAP_TRUNCATE,
                }}
              >
                {selectedContext?.name}
              </Typography>
              {!disabled &&
                (menuOpen ? (
                  <ArrowUpIcon className={classes.menuToggleIcon} />
                ) : (
                  <ArrowDownIcon className={classes.menuToggleIcon} />
                ))}
            </Flex>
          </Flex>
        </ButtonBase>
      )}
    </>
  );
};

/**
 * Individual root (org) or sub-context (fleet) menu item; supports text
 * wrapping and selected state
 */
const SelectableListItem = ({
  item,
  selectedContext,
  handleMenuItemClick,
  isRoot = false,
}: {
  item: ContextItem;
  selectedContext: ContextItem;
  handleMenuItemClick: (item: ContextItem) => void;
  isRoot?: boolean;
}) => {
  const { classes } = useStyles();
  return (
    <MenuItem
      data-testid={
        selectedContext.id === item.id
          ? 'fleet-switcher-menu-selected-item'
          : 'fleet-switcher-menu-item'
      }
      selected={selectedContext.id === item.id}
      onClick={(event) => {
        if (isRoot && event.type !== 'click') {
          return; // ignore spacebar keypresses being treated like clicks
        }
        handleMenuItemClick(item);
      }}
      className={cx(
        classes.selectableListItem,
        isRoot ? classes.rootListItem : classes.subContextListItem
      )}
    >
      <ListItemText
        primaryTypographyProps={{
          variant:
            selectedContext.id === item.id ? 'body_medium' : 'body_regular',
          sx: {
            ...SX_WRAP_TRUNCATE,
          },
        }}
        className={cx(
          selectedContext.id === item.id
            ? classes.selectedListItemText
            : classes.listItemText
        )}
      >
        {item.name}
      </ListItemText>
      {selectedContext.id === item.id && (
        <SelectedIcon
          data-testid="fleet-switcher-menu-selected-item-icon"
          className={classes.selectedItemIcon}
        />
      )}
    </MenuItem>
  );
};

/**
 * Allows users with access to multiple fleet contexts to specify which context the
 * app should use when displaying content which may be filtered/scoped by fleet
 *
 * @see {@link https://dev.azure.com/BrightDrop/BrightDrop/_workitems/edit/51953}
 *
 * @param contexts The available fleet `subContexts` and parent org `root` context
 * @param selectedContextId The currently-selected context
 * @param onContextSelect Handler triggered when a context is selected
 * @param navExpanded If nav is not expanded, display switcher icon only
 */
const FleetSwitcher = ({
  contexts,
  selectedContext,
  onContextSelect,
  navExpanded,
  disabled = false,
}: FleetSwitcherProps) => {
  const { classes } = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const menuListRef = useRef<HTMLUListElement | null>(null);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);

  const handleMenuOpen = (clicked: boolean) => {
    setMenuOpen(true);
    if (!clicked) {
      setFocusIndex(0);

      // explicitly focus the first item when opening
      requestAnimationFrame(() => {
        focusItem(0);
      });
    }
  };

  const handleMenuItemClick = (item: ContextItem) => {
    if (item.id !== selectedContext.id) {
      onContextSelect(item);
    }
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setFocusIndex(null);
  };

  /**
   * Handles key-down events within the switcher menu, specifically:
   *   - arrow keys: navigation of menu items
   *   - enter/space: selection of focused menu item
   *   - tab/escape: close menu without selection
   * @param event
   * @returns
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!menuOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      const nextIndex = Math.min(
        (focusIndex ?? 0) + 1,
        contexts.subContexts.length
      );
      setFocusIndex(nextIndex);
      focusItem(nextIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      const prevIndex = Math.max((focusIndex ?? 0) - 1, 0);
      setFocusIndex(prevIndex);
      focusItem(prevIndex);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
      event.preventDefault();

      if (focusIndex !== null) {
        const item =
          focusIndex === 0
            ? contexts.root
            : contexts.subContexts[focusIndex - 1];
        onContextSelect(item);
        handleMenuClose();
      }
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();

      handleMenuClose();
    }
  };

  const focusItem = (index: number) => {
    const menuItems =
      menuListRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']");
    const focusTarget = menuItems?.[index];

    if (focusTarget) {
      // ensure focused element is not outside of the visible scroll area before focusing
      scrollIntoView(focusTarget);
      focusTarget.focus();
    }
  };

  /**
   * Compares top and bottom of target element to top and bottom of scrollable container,
   * and adjusts the scroll position for the container to ensure the target element is fully visible
   *
   * Note: since `offsetTop` is measured relative to the item's `offsetParent`, this logic assumes
   * the `scrollBoxRef` element is the parent of the `targetItem` element.
   *
   * @param targetItem item that may need to be scrolled into view
   */
  const scrollIntoView = (targetItem: HTMLElement) => {
    if (!scrollBoxRef.current) return;

    const { offsetTop: targetItemTop, offsetHeight: targetItemHeight } =
      targetItem;

    const {
      scrollTop: scrollContainerTop,
      clientHeight: scrollContainerHeight,
    } = scrollBoxRef.current;

    if (targetItemTop < scrollContainerTop) {
      scrollBoxRef.current.scrollTop = targetItemTop;
    } else if (
      targetItemTop + targetItemHeight >
      scrollContainerTop + scrollContainerHeight
    ) {
      scrollBoxRef.current.scrollTop =
        targetItemTop + targetItemHeight - scrollContainerHeight;
    }
  };

  return (
    <Box
      data-testid="fleet-switcher"
      className={cx(classes.fleetSwitcherContainer, {
        [classes.disabled]: disabled,
      })}
      style={{ width: navExpanded ? `${NAV_DRAWER_OPEN_WIDTH}px` : 'auto' }}
    >
      <SwitcherMenuButton
        buttonRef={anchorEl}
        menuOpen={menuOpen}
        navExpanded={navExpanded}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
        selectedContext={selectedContext}
        disabled={disabled}
      />

      {/* fleet switcher menu */}
      <Popper
        data-testid="fleet-switcher-menu"
        open={menuOpen}
        anchorEl={anchorEl.current}
        placement={navExpanded ? 'bottom-start' : 'right-start'}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: navExpanded ? [0, 4] : [0, 16],
            },
          },
        ]}
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={handleMenuClose}>
          <Paper className={classes.paper}>
            <MenuList
              ref={menuListRef}
              role="menu"
              aria-labelledby="fleet-switcher"
              onKeyDown={handleKeyDown}
              disablePadding
              className={classes.menuList}
            >
              {/* sticky root context */}
              <SelectableListItem
                item={contexts.root}
                selectedContext={selectedContext}
                handleMenuItemClick={handleMenuItemClick}
                isRoot={true}
              />
              {/* scrollable sub-contexts */}
              <Box ref={scrollBoxRef} className={classes.scrollBox}>
                {contexts.subContexts.map((subContext) => (
                  <SelectableListItem
                    key={subContext.id}
                    item={subContext}
                    selectedContext={selectedContext}
                    handleMenuItemClick={handleMenuItemClick}
                  />
                ))}
              </Box>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

/**
 * Non-interactive component to be used when user has only a single available context
 *
 * @param selectedContext The currently-selected context
 * @param navExpanded If nav is not expanded, display switcher icon only
 */
export const StaticFleetSwitcher = ({
  navExpanded,
  selectedContext,
}: {
  navExpanded: boolean;
  selectedContext: ContextItem;
}) => {
  const { classes } = useStyles();
  return (
    <Box
      data-testid="static-fleet-switcher"
      className={classes.staticFleetSwitcherContainer}
      style={{ width: navExpanded ? `${NAV_DRAWER_OPEN_WIDTH}px` : 'auto' }}
    >
      {!navExpanded ? (
        <Tooltip
          content={selectedContext.name}
          placement="right"
          showArrow
          data-testid="static-fleet-switcher-tooltip"
          trigger={
            <Box>
              <Flex center className={classes.staticFleetSwitcherContent}>
                <Box className={classes.staticIconContainer}>
                  <OrganizationIcon className={classes.buttonIcon} />
                </Box>
              </Flex>
            </Box>
          }
        />
      ) : (
        <Flex gap={12} className={classes.staticFleetSwitcherContent}>
          <Box className={classes.staticIconContainer}>
            <OrganizationIcon className={classes.buttonIcon} />
          </Box>
          <Flex className={classes.staticExpandedContent}>
            <Typography
              variant="body_medium"
              sx={{
                ...SX_WRAP_TRUNCATE,
              }}
            >
              {selectedContext.name}
            </Typography>
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

export default FleetSwitcher;
